import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  cambiarEstatusProspectoCrm,
  crearActividadProspecto,
  ESTATUS_PROSPECTO_CRM,
  ESTATUS_PROSPECTO_LABELS,
  FUENTE_TIPO_LABELS,
  getCatalogoResponsables,
  labelFromMap,
  listarProspectosCrm,
  obtenerPendientesProspectos,
  ORIGENES_PROSPECTO,
  ORIGEN_LABELS,
  TEMPERATURAS_PROSPECTO,
  TEMPERATURA_LABELS,
  TIPOS_PROSPECTO_CRM,
  TIPO_PROSPECTO_LABELS,
} from '../services/prospectosCrmService';
import { obtenerAdminInmuebles } from '../services/adminInmueblesService';
import { listarAdminDesarrollos } from '../services/adminDesarrollosService';
import { listarProyectos } from '../services/proyectosInmobiliariosService';
import useAuthSession from '../hooks/useAuthSession';
import ProspectosPendientesCards from '../components/crm/ProspectosPendientesCards';
import {
  ProspectoEstatusBadge,
  ProspectoTemperaturaBadge,
  ProspectoTipoBadge,
} from '../components/crm/ProspectoBadges';
import ProspectoStatusModal from '../components/crm/ProspectoStatusModal';
import ProspectoActividadForm from '../components/crm/ProspectoActividadForm';
import './AdminProspectosPage.css';

const FILTROS_INICIALES = {
  busqueda: '',
  asesorId: '',
  supervisorId: '',
  empresaId: '',
  estatus: '',
  temperatura: '',
  tipoProspecto: '',
  origen: '',
  desarrolloId: '',
  proyectoId: '',
  inmuebleId: '',
  tipoOperacionBuscada: '',
  tipoInmuebleBuscado: '',
  sinAsignar: '',
  seguimientoVencido: '',
  fechaRegistroDesde: '',
  fechaRegistroHasta: '',
  ordenarPor: 'fechaRegistro',
  direccionOrden: 'desc',
  pagina: 1,
  tamanoPagina: 20,
};

const getApiErrorMessage = (err) =>
  err.data?.mensaje || err.data?.message || err.message || 'No fue posible procesar la accion.';

const getWhatsappUrl = (prospecto, asesorNombre) => {
  const digits = String(prospecto.telefonoNormalizado || prospecto.telefono || '').replace(/\D/g, '');
  if (!digits) return '';
  const normalized = digits.length === 10 ? `52${digits}` : digits;
  const text = `Hola ${prospecto.nombreContacto || prospecto.nombre}, soy ${asesorNombre || prospecto.asesorNombre || 'tu asesor'} de CN Inmobiliaria. Te contacto para dar seguimiento a tu busqueda.`;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(text)}`;
};

const getOrigenComercialTexto = (prospecto) => {
  if (!prospecto.asesorAtribucionNombre) return 'Sin referencia de asesor';
  if (prospecto.asesorAtribucionNombre === prospecto.asesorResponsableNombre) {
    return 'Mismo asesor de origen';
  }
  return `Asesor de origen: ${prospecto.asesorAtribucionNombre}`;
};

const buildFiltrosFromParams = (params) => ({
  ...FILTROS_INICIALES,
  ...Object.fromEntries(Object.keys(FILTROS_INICIALES).map((key) => [key, params.get(key) || FILTROS_INICIALES[key]])),
  pagina: Number(params.get('pagina') || FILTROS_INICIALES.pagina),
  tamanoPagina: Number(params.get('tamanoPagina') || FILTROS_INICIALES.tamanoPagina),
});

export default function AdminProspectosPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sesion = useAuthSession();
  const [filtros, setFiltros] = useState(() => buildFiltrosFromParams(searchParams));
  const [respuesta, setRespuesta] = useState({
    items: [],
    pagina: 1,
    tamanoPagina: 20,
    totalRegistros: 0,
    totalPaginas: 0,
  });
  const [pendientes, setPendientes] = useState(null);
  const [responsables, setResponsables] = useState({ asesores: [], supervisores: [] });
  const [inmuebles, setInmuebles] = useState([]);
  const [desarrollos, setDesarrollos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [mostrarMasFiltros, setMostrarMasFiltros] = useState(false);
  const [statusModal, setStatusModal] = useState(null);
  const [actividadRapida, setActividadRapida] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);
  const [cargandoResponsables, setCargandoResponsables] = useState(true);
  const [errorResponsables, setErrorResponsables] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const puedeReasignar = ['ADMIN', 'SUPERADMIN', 'SUPERVISOR'].includes(String(sesion.rolGlobal || '').toUpperCase()) || sesion.esAdminCn;

  const filtrosConsulta = useMemo(() => ({
    ...filtros,
    pagina: Number(filtros.pagina) || 1,
    tamanoPagina: Number(filtros.tamanoPagina) || 20,
    sinAsignar: filtros.sinAsignar === 'true' ? true : '',
    seguimientoVencido: filtros.seguimientoVencido === 'true' ? true : '',
  }), [filtros]);

  const cargarProspectos = useCallback(async (options = {}) => {
    setCargando(true);
    setError('');

    try {
      const data = await listarProspectosCrm({ ...filtrosConsulta, signal: options.signal });
      setRespuesta(data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(getApiErrorMessage(err));
      }
    } finally {
      if (!options.signal?.aborted) {
        setCargando(false);
      }
    }
  }, [filtrosConsulta]);

  const cargarPendientes = useCallback(async (options = {}) => {
    try {
      setPendientes(await obtenerPendientesProspectos(options));
    } catch (_) {
      setPendientes(null);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    cargarProspectos({ signal: controller.signal });
    cargarPendientes({ signal: controller.signal });
    return () => controller.abort();
  }, [cargarPendientes, cargarProspectos]);

  const cargarResponsables = useCallback(async (options = {}) => {
    setCargandoResponsables(true);
    setErrorResponsables('');

    try {
      setResponsables(await getCatalogoResponsables(options));
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('No fue posible cargar responsables CRM', err);
        setErrorResponsables('No fue posible cargar responsables comerciales.');
      }
    } finally {
      if (!options.signal?.aborted) {
        setCargandoResponsables(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const cargarCatalogos = async () => {
      setCargandoCatalogos(true);
      try {
        const [inmueblesData, desarrollosData, proyectosData] = await Promise.all([
          obtenerAdminInmuebles({ signal: controller.signal }).catch(() => []),
          listarAdminDesarrollos({ signal: controller.signal }).catch(() => []),
          listarProyectos({ soloActivos: true, signal: controller.signal }).catch(() => []),
        ]);
        setInmuebles(inmueblesData);
        setDesarrollos(desarrollosData);
        setProyectos(proyectosData);
      } finally {
        if (!controller.signal.aborted) {
          setCargandoCatalogos(false);
        }
      }
    };

    cargarCatalogos();
    cargarResponsables({ signal: controller.signal });
    return () => controller.abort();
  }, [cargarResponsables]);

  const syncParams = (nextFiltros) => {
    const params = {};
    Object.entries(nextFiltros).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null && String(value) !== String(FILTROS_INICIALES[key])) {
        params[key] = String(value);
      }
    });
    setSearchParams(params);
  };

  const updateFiltro = (event) => {
    const { name, value } = event.target;
    setFiltros((actual) => ({ ...actual, [name]: value, pagina: 1 }));
  };

  const aplicarFiltros = (event) => {
    event.preventDefault();
    syncParams(filtros);
    cargarProspectos();
  };

  const limpiarFiltros = () => {
    setFiltros(FILTROS_INICIALES);
    setSearchParams({});
  };

  const aplicarFiltroRapido = (patch) => {
    const next = { ...filtros, ...patch, pagina: 1 };
    setFiltros(next);
    syncParams(next);
  };

  const cambiarPagina = (pagina) => {
    const next = { ...filtros, pagina };
    setFiltros(next);
    syncParams(next);
  };

  const cambiarTamanoPagina = (event) => {
    const next = { ...filtros, tamanoPagina: Number(event.target.value), pagina: 1 };
    setFiltros(next);
    syncParams(next);
  };

  const guardarEstatus = async (payload) => {
    if (!statusModal?.id) return;
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      await cambiarEstatusProspectoCrm(statusModal.id, payload);
      setMensaje('Estatus actualizado correctamente.');
      setStatusModal(null);
      await Promise.all([cargarProspectos(), cargarPendientes()]);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  const guardarActividadRapida = async (payload) => {
    if (!actividadRapida?.id) return false;
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      await crearActividadProspecto(actividadRapida.id, payload);
      setMensaje('Actividad registrada correctamente.');
      setActividadRapida(null);
      await Promise.all([cargarProspectos(), cargarPendientes()]);
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err));
      return false;
    } finally {
      setGuardando(false);
    }
  };

  return (
    <main className="crm-prospectos">
      <section className="crm-prospectos-hero">
        <div>
          <p className="crm-prospectos-eyebrow">CRM central</p>
          <h1>Prospectos</h1>
          <span>Gestiona contactos, seguimientos y oportunidades comerciales.</span>
        </div>
        <div className="crm-prospectos-hero-actions">
          <button type="button" onClick={() => aplicarFiltroRapido({ seguimientoVencido: 'true' })}>Mis pendientes</button>
          <Link className="crm-prospectos-primary" to="/admin/prospectos/nuevo">+ Nuevo prospecto</Link>
        </div>
      </section>

      <ProspectosPendientesCards pendientes={pendientes} onFilter={aplicarFiltroRapido} />

      <form className="crm-prospectos-filtros" onSubmit={aplicarFiltros}>
        <label className="is-wide">
          <span>Busqueda libre</span>
          <input name="busqueda" value={filtros.busqueda} onChange={updateFiltro} placeholder="Nombre, telefono, correo o interes" />
        </label>
        <label>
          <span>Estatus</span>
          <select name="estatus" value={filtros.estatus} onChange={updateFiltro}>
            <option value="">Todos</option>
            {ESTATUS_PROSPECTO_CRM.map((estatus) => <option key={estatus} value={estatus}>{labelFromMap(ESTATUS_PROSPECTO_LABELS, estatus)}</option>)}
          </select>
        </label>
        <label>
          <span>Temperatura</span>
          <select name="temperatura" value={filtros.temperatura} onChange={updateFiltro}>
            <option value="">Todas</option>
            {TEMPERATURAS_PROSPECTO.map((temperatura) => <option key={temperatura} value={temperatura}>{labelFromMap(TEMPERATURA_LABELS, temperatura)}</option>)}
          </select>
        </label>
        <label>
          <span>Tipo de prospecto</span>
          <select name="tipoProspecto" value={filtros.tipoProspecto} onChange={updateFiltro}>
            <option value="">Todos</option>
            {TIPOS_PROSPECTO_CRM.map((tipo) => <option key={tipo} value={tipo}>{labelFromMap(TIPO_PROSPECTO_LABELS, tipo)}</option>)}
          </select>
        </label>
        <label>
          <span>Origen</span>
          <select name="origen" value={filtros.origen} onChange={updateFiltro}>
            <option value="">Todos</option>
            {ORIGENES_PROSPECTO.map((origen) => <option key={origen} value={origen}>{labelFromMap(ORIGEN_LABELS, origen)}</option>)}
          </select>
        </label>
        <label>
          <span>Responsable</span>
          <select name="asesorId" value={filtros.asesorId} onChange={updateFiltro} disabled={cargandoResponsables}>
            <option value="">Todos</option>
            {responsables.asesores.map((asesor) => <option key={asesor.id} value={asesor.id}>{asesor.nombre}</option>)}
          </select>
          {cargandoResponsables ? <small>Cargando responsables...</small> : null}
        </label>
        <label>
          <span>Desarrollo / proyecto / propiedad</span>
          <select name="proyectoId" value={filtros.proyectoId} onChange={updateFiltro} disabled={cargandoCatalogos}>
            <option value="">Todos los proyectos</option>
            {proyectos.map((proyecto) => <option key={proyecto.id} value={proyecto.id}>{proyecto.nombre}</option>)}
          </select>
        </label>
        <label className="crm-prospectos-check">
          <input name="sinAsignar" type="checkbox" checked={filtros.sinAsignar === 'true'} onChange={(event) => updateFiltro({ target: { name: 'sinAsignar', value: event.target.checked ? 'true' : '' } })} />
          <span>Solo sin asignar</span>
        </label>
        <label className="crm-prospectos-check">
          <input name="seguimientoVencido" type="checkbox" checked={filtros.seguimientoVencido === 'true'} onChange={(event) => updateFiltro({ target: { name: 'seguimientoVencido', value: event.target.checked ? 'true' : '' } })} />
          <span>Seguimientos vencidos</span>
        </label>
        <label>
          <span>Registro desde</span>
          <input name="fechaRegistroDesde" type="date" value={filtros.fechaRegistroDesde} onChange={updateFiltro} />
        </label>
        <label>
          <span>Registro hasta</span>
          <input name="fechaRegistroHasta" type="date" value={filtros.fechaRegistroHasta} onChange={updateFiltro} />
        </label>

        {mostrarMasFiltros ? (
          <>
            <label>
              <span>Supervisor</span>
              <select name="supervisorId" value={filtros.supervisorId} onChange={updateFiltro} disabled={cargandoResponsables}>
                <option value="">Todos</option>
                {responsables.supervisores.map((supervisor) => <option key={supervisor.id} value={supervisor.id}>{supervisor.nombre}</option>)}
              </select>
              {cargandoResponsables ? <small>Cargando responsables...</small> : null}
            </label>
            <label>
              <span>Desarrollo</span>
              <select name="desarrolloId" value={filtros.desarrolloId} onChange={updateFiltro} disabled={cargandoCatalogos}>
                <option value="">Todos</option>
                {desarrollos.map((desarrollo) => <option key={desarrollo.id} value={desarrollo.id}>{desarrollo.nombre}</option>)}
              </select>
            </label>
            <label>
              <span>Propiedad</span>
              <select name="inmuebleId" value={filtros.inmuebleId} onChange={updateFiltro} disabled={cargandoCatalogos}>
                <option value="">Todas</option>
                {inmuebles.map((inmueble) => <option key={inmueble.id} value={inmueble.id}>{inmueble.titulo}</option>)}
              </select>
            </label>
            <label>
              <span>Ordenar por</span>
              <select name="ordenarPor" value={filtros.ordenarPor} onChange={updateFiltro}>
                <option value="fechaRegistro">Fecha de registro</option>
                <option value="fechaProximoSeguimiento">Proximo seguimiento</option>
                <option value="fechaUltimaActividad">Ultima actividad</option>
                <option value="temperatura">Temperatura</option>
              </select>
            </label>
            <label>
              <span>Direccion</span>
              <select name="direccionOrden" value={filtros.direccionOrden} onChange={updateFiltro}>
                <option value="desc">Descendente</option>
                <option value="asc">Ascendente</option>
              </select>
            </label>
          </>
        ) : null}

        <div className="crm-prospectos-filtros-actions">
          <button type="submit" className="is-primary">Aplicar filtros</button>
          <button type="button" onClick={() => setMostrarMasFiltros((value) => !value)}>{mostrarMasFiltros ? 'Menos filtros' : 'Mas filtros'}</button>
          <button type="button" onClick={limpiarFiltros}>Limpiar</button>
        </div>
      </form>

      {errorResponsables ? (
        <p className="crm-prospectos-feedback is-error">
          <span>{errorResponsables}</span>
          <button type="button" onClick={() => cargarResponsables()}>Reintentar</button>
        </p>
      ) : null}
      {mensaje ? <p className="crm-prospectos-feedback is-ok">{mensaje}</p> : null}
      {error ? (
        <div className="crm-prospectos-feedback is-error">
          <span>{error}</span>
          <button type="button" onClick={() => cargarProspectos()}>Reintentar</button>
        </div>
      ) : null}
      {cargando ? <p className="crm-prospectos-feedback">Cargando prospectos...</p> : null}

      {!cargando && !error ? (
        <section className="crm-prospectos-card">
          {respuesta.items.length === 0 ? (
            <p className="crm-prospectos-empty">
              {Object.values(filtros).some((value) => value && value !== 1 && value !== 20 && value !== 'fechaRegistro' && value !== 'desc')
                ? 'No hay prospectos con los filtros seleccionados.'
                : 'Aun no hay prospectos registrados.'}
            </p>
          ) : (
            <>
              <div className="crm-prospectos-table-wrap">
                <table className="crm-prospectos-table">
                  <thead>
                    <tr>
                      <th>Prospecto</th>
                      <th>Tipo</th>
                      <th>Interes / busqueda</th>
                      <th>Fuente / origen</th>
                      <th>Responsable</th>
                      <th>Estatus</th>
                      <th>Temperatura</th>
                      <th>Proximo seguimiento</th>
                      <th>Ultima actividad</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {respuesta.items.map((prospecto) => {
                      const whatsappUrl = getWhatsappUrl(prospecto, sesion.nombre);

                      return (
                        <tr key={prospecto.id}>
                          <td data-label="Prospecto">
                            <div className="crm-prospectos-persona">
                              <strong>{prospecto.nombre}</strong>
                              <span>{prospecto.telefono || 'Sin telefono'}</span>
                              {prospecto.email ? <span>{prospecto.email}</span> : null}
                              {prospecto.tieneDuplicados ? <small>Posible duplicado</small> : null}
                            </div>
                          </td>
                          <td data-label="Tipo"><ProspectoTipoBadge tipo={prospecto.tipoProspecto} /></td>
                          <td data-label="Interes / busqueda">{prospecto.interes}</td>
                          <td data-label="Fuente / origen">
                            <div className="crm-prospectos-persona">
                              <strong>{labelFromMap(FUENTE_TIPO_LABELS, prospecto.fuenteTipo, 'Fuente sin definir')}</strong>
                              <span>{labelFromMap(ORIGEN_LABELS, prospecto.origen)}</span>
                            </div>
                          </td>
                          <td data-label="Responsable">
                            <div className="crm-prospectos-persona">
                              <strong>{prospecto.asesorResponsableNombre || 'Sin asignar'}</strong>
                              <small>{getOrigenComercialTexto(prospecto)}</small>
                              {prospecto.supervisorNombre ? <span>Sup. {prospecto.supervisorNombre}</span> : null}
                            </div>
                          </td>
                          <td data-label="Estatus"><ProspectoEstatusBadge estatus={prospecto.estatus} /></td>
                          <td data-label="Temperatura"><ProspectoTemperaturaBadge temperatura={prospecto.temperatura} /></td>
                          <td data-label="Proximo seguimiento">
                            <span className={prospecto.seguimientoVencido ? 'crm-prospectos-date is-overdue' : 'crm-prospectos-date'}>
                              {prospecto.fechaProximoSeguimiento}
                            </span>
                          </td>
                          <td data-label="Ultima actividad">
                            <div className="crm-prospectos-persona">
                              <span>{prospecto.fechaUltimaActividad}</span>
                              {prospecto.tipoUltimaActividad ? <small>{prospecto.tipoUltimaActividad}</small> : null}
                            </div>
                          </td>
                          <td data-label="Acciones">
                            <div className="crm-prospectos-actions">
                              <button type="button" onClick={() => navigate(`/admin/prospectos/${prospecto.id}`)}>Abrir ficha</button>
                              {whatsappUrl ? <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">WhatsApp</a> : <button type="button" disabled title="Sin telefono">WhatsApp</button>}
                              <button type="button" onClick={() => setStatusModal(prospecto)}>Cambiar estatus</button>
                              <button type="button" onClick={() => setActividadRapida(prospecto)}>Seguimiento</button>
                              {puedeReasignar ? <button type="button" onClick={() => navigate(`/admin/prospectos/${prospecto.id}?accion=reasignar`)}>Reasignar</button> : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="crm-prospectos-pagination">
                <span>{respuesta.totalRegistros} registros</span>
                <button type="button" onClick={() => cambiarPagina(Math.max(1, respuesta.pagina - 1))} disabled={respuesta.pagina <= 1}>Anterior</button>
                <strong>Pagina {respuesta.pagina} de {respuesta.totalPaginas || 1}</strong>
                <button type="button" onClick={() => cambiarPagina(Math.min(respuesta.totalPaginas || 1, respuesta.pagina + 1))} disabled={respuesta.pagina >= (respuesta.totalPaginas || 1)}>Siguiente</button>
                <select value={filtros.tamanoPagina} onChange={cambiarTamanoPagina} aria-label="Tamano de pagina">
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
            </>
          )}
        </section>
      ) : null}

      {statusModal ? (
        <ProspectoStatusModal
          prospecto={statusModal}
          onClose={() => setStatusModal(null)}
          onSubmit={guardarEstatus}
          saving={guardando}
        />
      ) : null}

      {actividadRapida ? (
        <div className="crm-prospectos-modal-overlay" role="presentation" onMouseDown={() => setActividadRapida(null)}>
          <section className="crm-prospectos-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className="crm-prospectos-modal-head">
              <div>
                <p className="crm-prospectos-eyebrow">Seguimiento</p>
                <h2>{actividadRapida.nombre}</h2>
              </div>
              <button type="button" onClick={() => setActividadRapida(null)} disabled={guardando} aria-label="Cerrar">x</button>
            </div>
            <div className="crm-prospectos-modal-body">
              <ProspectoActividadForm onSubmit={guardarActividadRapida} saving={guardando} />
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
